import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Heart, Loader2 } from 'lucide-react';

const SPECIALIZATIONS = [
  'Emergency Care',
  'Critical Care / ICU',
  'Pediatrics',
  'Oncology',
  'Surgery',
  'Mental Health',
  'Geriatrics',
  'Cardiology',
  'Labor & Delivery',
  'Home Health',
  'Other'
];

const RegisterPage = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    specialization: '',
    experience_years: '',
    bio: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    const result = await register({
      email: formData.email,
      password: formData.password,
      name: formData.name,
      specialization: formData.specialization || null,
      experience_years: formData.experience_years ? parseInt(formData.experience_years) : null,
      bio: formData.bio || null
    });

    setLoading(false);

    if (result.success) {
      navigate('/');
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative">
        <img
          src="https://customer-assets.emergentagent.com/job_scrubs-social/artifacts/ci7c18vn_ChatGPT%20Image%20Mar%2028%2C%202026%2C%2010_51_14%20PM.png"
          alt="Diverse nursing team"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/30 to-transparent" />
        <div className="absolute bottom-12 left-12 text-white max-w-md">
          <h2 className="text-4xl font-bold font-['Outfit'] mb-4">Join Our Community</h2>
          <p className="text-lg opacity-90">Connect with thousands of nurses worldwide. Share experiences, learn, and grow together.</p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-[#F7F5F2] overflow-y-auto">
        <Card className="w-full max-w-md border-stone-200 rounded-2xl shadow-none my-8">
          <CardHeader className="text-center pb-2">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Heart className="h-8 w-8 text-[#6B8E7B]" fill="#6B8E7B" />
              <div>
                <span className="text-2xl font-bold font-['Outfit'] text-stone-800">NurseCommunity</span>
                <span className="text-xs text-stone-400 block">by Gina Rena</span>
              </div>
            </div>
            <CardTitle className="text-2xl font-['Outfit']">Create your account</CardTitle>
            <CardDescription className="text-stone-500">Join the community of nursing professionals</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="p-3 rounded-xl bg-red-50 text-red-600 text-sm" data-testid="register-error">
                  {error}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="name">Full Name *</Label>
                <Input
                  id="name"
                  name="name"
                  placeholder="Jane Doe"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="rounded-xl"
                  data-testid="register-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="nurse@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="rounded-xl"
                  data-testid="register-email-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="password">Password *</Label>
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    className="rounded-xl"
                    data-testid="register-password-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm *</Label>
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    placeholder="••••••••"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    required
                    className="rounded-xl"
                    data-testid="register-confirm-password-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="specialization">Specialization</Label>
                <Select
                  value={formData.specialization}
                  onValueChange={(value) => setFormData({ ...formData, specialization: value })}
                >
                  <SelectTrigger className="rounded-xl" data-testid="register-specialization-select">
                    <SelectValue placeholder="Select your specialty" />
                  </SelectTrigger>
                  <SelectContent>
                    {SPECIALIZATIONS.map((spec) => (
                      <SelectItem key={spec} value={spec}>{spec}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="experience_years">Years of Experience</Label>
                <Input
                  id="experience_years"
                  name="experience_years"
                  type="number"
                  min="0"
                  max="50"
                  placeholder="5"
                  value={formData.experience_years}
                  onChange={handleChange}
                  className="rounded-xl"
                  data-testid="register-experience-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="bio">Short Bio</Label>
                <Textarea
                  id="bio"
                  name="bio"
                  placeholder="Tell us a bit about yourself..."
                  value={formData.bio}
                  onChange={handleChange}
                  className="rounded-xl resize-none"
                  rows={3}
                  data-testid="register-bio-input"
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-[#6B8E7B] hover:bg-[#5A7868] rounded-full h-11"
                disabled={loading}
                data-testid="register-submit-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating account...
                  </>
                ) : (
                  'Create account'
                )}
              </Button>
            </form>

            <div className="mt-6 text-center text-sm text-stone-500">
              Already have an account?{' '}
              <Link to="/login" className="text-[#6B8E7B] hover:underline font-medium" data-testid="login-link">
                Sign in
              </Link>
            </div>
            <div className="mt-4 text-center text-xs text-stone-400">
              By creating an account, you agree to our{' '}
              <Link to="/terms" className="hover:text-[#6B8E7B] underline">Terms</Link>
              {' '}and{' '}
              <Link to="/privacy" className="hover:text-[#6B8E7B] underline">Privacy Policy</Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default RegisterPage;
